import { zip } from 'es-toolkit';

import { BearingEnvelope, EnvelopeJustification, TrussEnvelope } from '@paragon/contract-library';
import {
  convertDegreesToRadians,
  Direction2D,
  distanceEquals,
  LineSegment2D,
  point2DEquals,
  Polygon2D,
  Vector2D,
} from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getEnvelopeSegment, getEnvelopeSegmentWithOverhangs } from './getEnvelopeSegment';

export interface EnvelopeThicknessProperties {
  readonly justification: EnvelopeJustification;
  readonly thickness: number;
  readonly numberOfPlies?: number;
}

export type TrussEnvelopeThicknessProperties = Required<EnvelopeThicknessProperties>;

export type TrussEnvelopeBasicShapeProperties = Pick<
  TrussEnvelope,
  'leftPoint' | 'rightPoint' | 'leftOverhang' | 'rightOverhang'
> &
  TrussEnvelopeThicknessProperties;

function calculateEnvelopeShape<T extends TrussEnvelopeBasicShapeProperties | BearingEnvelope>(envelope: T) {
  const definingSegment = getEnvelopeSegment(envelope);
  if (!definingSegment) return null;

  const backSegment = getEnvelopeSegmentFromDefiningSegment(definingSegment, envelope, 'Back');
  const frontSegment = getEnvelopeSegmentFromDefiningSegment(definingSegment, envelope, 'Front');

  if (point2DEquals(backSegment.basePoint, frontSegment.basePoint)) return null;

  if (!('leftOverhang' in envelope) || !('rightOverhang' in envelope))
    return new Polygon2D(
      [backSegment.endPoint, backSegment.basePoint, frontSegment.basePoint, frontSegment.endPoint],
      false,
    );

  const envelopeRight = definingSegment.direction;
  return new Polygon2D(
    [
      backSegment.endPoint.translate(envelopeRight.toVector(envelope.rightOverhang)),
      backSegment.basePoint.translate(envelopeRight.toVector(-envelope.leftOverhang)),
      frontSegment.basePoint.translate(envelopeRight.toVector(-envelope.leftOverhang)),
      frontSegment.endPoint.translate(envelopeRight.toVector(envelope.rightOverhang)),
    ],
    false,
  );
}
export const calculateBearingEnvelopeShape = calculateEnvelopeShape<BearingEnvelope>;
export const calculateTrussEnvelopeShapeWithoutBevels = calculateEnvelopeShape<TrussEnvelopeBasicShapeProperties>;

export function getOffsetVectorForEnvelopeSegment(
  definingSegmentDirection: Direction2D,
  thicknessProperties: EnvelopeThicknessProperties,
  segmentLocation: EnvelopeJustification,
) {
  if (thicknessProperties.justification === segmentLocation) return new Vector2D(0, 0);

  const justificationToCoefficient = (justification: EnvelopeJustification) =>
    justification === 'Back'
      ? 0
      : justification === 'Center'
        ? 0.5
        : justification === 'Front'
          ? 1
          : assertExhaustiveSwitch(justification);

  const deltaThickness =
    (justificationToCoefficient(segmentLocation) - justificationToCoefficient(thicknessProperties.justification)) *
    thicknessProperties.thickness *
    (thicknessProperties.numberOfPlies ?? 1);

  return Vector2D.fromDirectionAndMagnitude(definingSegmentDirection.rotate(270), deltaThickness);
}

export function getEnvelopeSegmentFromDefiningSegment(
  definingSegment: LineSegment2D,
  thicknessProperties: EnvelopeThicknessProperties,
  segmentLocation: EnvelopeJustification,
) {
  return definingSegment.translate(
    getOffsetVectorForEnvelopeSegment(definingSegment.direction, thicknessProperties, segmentLocation),
  );
}

export function getEnvelopeSegmentFromEnvelope(
  trussEnvelope: TrussEnvelope | BearingEnvelope,
  segmentLocation: EnvelopeJustification,
) {
  const definingSegment = getEnvelopeSegment(trussEnvelope);
  return definingSegment != null
    ? getEnvelopeSegmentFromDefiningSegment(definingSegment, trussEnvelope, segmentLocation)
    : null;
}

export function getEnvelopeSegmentWithOverhangsFromEnvelope(
  trussEnvelope: TrussEnvelope,
  segmentLocation: EnvelopeJustification,
) {
  const definingSegment = getEnvelopeSegmentWithOverhangs(trussEnvelope);
  return definingSegment != null
    ? getEnvelopeSegmentFromDefiningSegment(definingSegment, trussEnvelope, segmentLocation)
    : null;
}

export interface TrussEnvelopeEdgeSegments {
  readonly backSegment: LineSegment2D;
  readonly frontSegment: LineSegment2D;
  readonly middleSegment: LineSegment2D;
}

export interface TrussEnvelopeShapeWithEdgeSegments extends TrussEnvelopeEdgeSegments {
  readonly shape: Polygon2D;
}

export function calculateTrussEnvelopeShapeWithEdgeSegments(
  envelope: TrussEnvelope,
): TrussEnvelopeShapeWithEdgeSegments | null {
  const getPoints = (segment: LineSegment2D, side: 'Left' | 'Right') => {
    const overhang = side === 'Left' ? envelope.leftOverhang : envelope.rightOverhang;
    const bevelCut = side === 'Left' ? envelope.leftBevelCut : envelope.rightBevelCut;
    const trimCoefficients: [number, number, number] = !distanceEquals(overhang, 0)
      ? [1, 1, 1]
      : bevelCut == null
        ? [0, 0, 0]
        : bevelCut.type === 'Back'
          ? [1, 0.5, 0]
          : bevelCut.type === 'Double'
            ? [0.5, 0, 0.5]
            : bevelCut.type === 'Front'
              ? [0, 0.5, 1]
              : assertExhaustiveSwitch(bevelCut.type);

    const trimDistance = !distanceEquals(overhang, 0)
      ? -overhang
      : bevelCut == null
        ? 0
        : Math.tan(convertDegreesToRadians(bevelCut.angle)) * envelope.thickness;

    const trimVector = Vector2D.fromDirectionAndMagnitude(segment.direction, (side === 'Left' ? 1 : -1) * trimDistance);
    return zip(
      (['Back', 'Center', 'Front'] as const)
        .map((justification) => getEnvelopeSegmentFromDefiningSegment(segment, envelope, justification))
        .map(({ basePoint, endPoint }) => (side === 'Left' ? basePoint : endPoint)),
      trimCoefficients,
    ).map(([point, trimCoefficient]) => trimVector.multiplyScalar(trimCoefficient!).imageOfOrigin.add(point!));
  };

  const segment = getEnvelopeSegment(envelope);
  if (segment == null) return null;

  const leftPoints = getPoints(segment, 'Left');
  const rightPoints = getPoints(segment, 'Right');

  if (zip(leftPoints, rightPoints).some(([left, right]) => !left || !right || left.equals(right))) {
    return null;
  }

  const backSegment = new LineSegment2D(leftPoints[0], rightPoints[0]);
  const middleSegment = new LineSegment2D(leftPoints[1], rightPoints[1]);
  const frontSegment = new LineSegment2D(leftPoints[2], rightPoints[2]);

  const shape = new Polygon2D([...leftPoints, ...rightPoints.toReversed()]);

  return { shape, backSegment, frontSegment, middleSegment };
}

export function calculateTrussEnvelopeShape(envelope: TrussEnvelope): Polygon2D | null {
  return calculateTrussEnvelopeShapeWithEdgeSegments(envelope)?.shape ?? null;
}

export function calculatePlyDividerSegments(envelope: TrussEnvelope | BearingEnvelope): LineSegment2D[] {
  if (envelope.numberOfPlies < 2) return [];

  const backSegment =
    'leftOverhang' in envelope && 'rightOverhang' in envelope
      ? getEnvelopeSegmentWithOverhangsFromEnvelope(envelope, 'Back')
      : getEnvelopeSegmentFromEnvelope(envelope, 'Back');

  if (backSegment == null) return [];

  const perpendicular = backSegment.direction.rotateReverseQuarterTurn();

  return Array.from({ length: envelope.numberOfPlies - 1 }, (_, i) =>
    backSegment.translate(Vector2D.fromDirectionAndMagnitude(perpendicular, (i + 1) * envelope.thickness)),
  );
}
